import { useEffect, useId, useRef, useState } from 'react'
import { api } from '../../api/client'
import EngagementAnswerReview from './EngagementAnswerReview'

const display = (value: unknown): string => value === null || value === undefined ? 'Not provided' : typeof value === 'string' ? value : JSON.stringify(value, null, 2)

export default function EngagementAnswers({engagementId, companyId, companyName}: {engagementId: string; companyId: string; companyName: string}) {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<Awaited<ReturnType<typeof api.engagementAnswers>> | null>(null)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const panelId = useId()
  const sequence = useRef(0)
  useEffect(() => {
    if (!open) return
    let live = true
    let timer: number | undefined
    let binding: string | undefined
    const refresh = async () => {
      const request = ++sequence.current
      try {
        const result = await api.engagementAnswers(engagementId, companyId, binding)
        if (live && request === sequence.current) { binding = result.binding_hash; setData(result); setError('') }
      } catch (e) { if (live) setError(e instanceof Error ? e.message : 'Could not load engagement answers') }
      finally { if (live) timer = window.setTimeout(refresh, 5000) }
    }
    void refresh()
    return () => { live = false; window.clearTimeout(timer) }
  }, [engagementId, companyId, open, attempt])
  return <>
    <button className="btn secondary small" aria-expanded={open} aria-controls={panelId} aria-label={`${open ? 'Hide' : 'View'} engagement answers for ${companyName}`} onClick={() => { setData(null); setError(''); setOpen(!open) }}>{open ? 'Hide engagement answers' : 'Engagement answers'}</button>
    {open && <div id={panelId} role="region" aria-label={`Engagement answers for ${companyName}`}>
      <p className="muted">Answers for the assigned criteria at opening. Analyst edits require a logged reason. Answers from previous inquiry criteria are not copied here. Vendor statements require review.</p>
      {error && <p className="notice crit" role="alert">{error} Displayed answers may be out of date.</p>}
      {!data && !error && <p className="muted">Loading engagement answers…</p>}
      {data && <>
        {!data.rows.length && <p className="muted">No answers recorded for these criteria yet.</p>}
        <div className="workflow-list">{data.rows.map(answer => <article className="workflow-card" key={answer.field_id}>
          <strong title={answer.field_id}>{answer.question || 'RFP answer'}</strong>
          <p>{display(answer.value)}</p>
          <p className="muted">{answer.status.replace(/_/g, ' ')} · {answer.verification?.replace(/_/g, ' ') || 'Vendor reported'}{answer.confidence !== null && answer.confidence !== undefined ? ` · ${Math.round(answer.confidence * 100)}% extraction confidence` : ' · Confidence not assessed'}</p>
          {answer.quote && <blockquote>{answer.quote}</blockquote>}
          {answer.timestamp && <p className="muted">{new Date(answer.timestamp).toLocaleString()}</p>}
          <EngagementAnswerReview engagementId={engagementId} companyId={companyId} bindingHash={data.binding_hash} answer={answer} conflicts={data.conflicts.filter(conflict => conflict.field_id === answer.field_id)} current={data.is_current} reload={async () => {
            const request = ++sequence.current
            const result = await api.engagementAnswers(engagementId, companyId, data.binding_hash)
            if (request === sequence.current) setData(result)
            return result.rows.find(row => row.field_id === answer.field_id)
          }} />
          <details><summary>Answer evidence record</summary><pre className="compiled-prompt">{JSON.stringify(answer, null, 2)}</pre></details>
        </article>)}</div>
        <h4>Conflicting statements</h4>
        {!data.conflicts.length && <p className="muted">No conflicts recorded for these criteria.</p>}
        {data.conflicts.map((conflict, index) => <article className="workflow-card" key={conflict.id || index}>
          <strong title={conflict.field_id}>{conflict.question || 'Conflicting RFP answer'}</strong>
          <p className="muted">{conflict.resolution ? `Resolved by ${conflict.resolution.actor} · ${new Date(conflict.resolution.created_at).toLocaleString()}` : 'The previous answer is retained for review.'}</p>
          <p><strong>Previous:</strong> {display(conflict.old_data.value)}</p>
          {conflict.old_data.quote && <blockquote>{conflict.old_data.quote}</blockquote>}
          <p><strong>New statement:</strong> {display(conflict.new_data.value)}</p>
          {conflict.new_data.quote && <blockquote>{conflict.new_data.quote}</blockquote>}
          <details><summary>Compare conflicting statements</summary><pre className="compiled-prompt">{JSON.stringify(conflict, null, 2)}</pre></details>
        </article>)}
        <details><summary>Criteria scope and review state</summary><pre className="compiled-prompt">{JSON.stringify({binding_hash: data.binding_hash, state: data.state}, null, 2)}</pre></details>
      </>}
      {error && <button className="btn secondary small" onClick={() => { setData(null); setError(''); setAttempt(value => value + 1) }}>Reload current answers</button>}
    </div>}
  </>
}
